/**
 * Evidence-based account severity classification (frontend mirror of backend).
 *
 * Severity priority: negative > bureau_difference > current > unknown
 * Bureau differences alone must never mark an account negative.
 * Random REVIEW tags without evidence are not allowed.
 */

import {
  isChargeOffStatus,
  isCollectionStatus,
  isLatePaymentStatus,
  isNegativePaymentStatus,
  normalizePaymentStatus,
  paymentStatusSeverity,
} from './paymentHistoryUtils';

export const SEVERITY_NEGATIVE = 'negative';
export const SEVERITY_BUREAU_DIFFERENCE = 'bureau_difference';
export const SEVERITY_CURRENT = 'current';
export const SEVERITY_UNKNOWN = 'unknown';

const BUREAU_COMPARE_FIELDS = [
  'balance',
  'credit_limit',
  'high_credit',
  'date_opened',
  'last_reported',
  'date_reported',
  'date_last_active',
  'date_of_last_payment',
  'date_last_payment',
  'remarks',
  'comments',
  'account_status',
  'status',
  'payment_status',
];

const LATE_COUNT_FIELDS = [
  'times_late',
  'late_payments',
  '30_day_late',
  '60_day_late',
  '90_day_late',
  'times_30_days_late',
  'times_60_days_late',
  'times_90_days_late',
];

const MONTH_LABELS = {
  1: 'Jan',
  '01': 'Jan',
  2: 'Feb',
  '02': 'Feb',
  3: 'Mar',
  '03': 'Mar',
  4: 'Apr',
  '04': 'Apr',
  5: 'May',
  '05': 'May',
  6: 'Jun',
  '06': 'Jun',
  7: 'Jul',
  '07': 'Jul',
  8: 'Aug',
  '08': 'Aug',
  9: 'Sep',
  '09': 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec',
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const accountName = (account) =>
  account?.account_name
  || account?.creditor_name
  || account?.creditor
  || account?.subscriber_name
  || 'Unknown';

const isCleanPositiveStatus = (value) => {
  const text = normalizeText(value);
  if (!text) return false;
  return /^(current|paid|pays?\s+as\s+agreed|paid\s+as\s+agreed|ok|open|active|closed|never\s+late)$/i.test(text);
};

const bureauFieldMap = (account, field) => {
  const bureauData = account?.bureau_data;
  if (!bureauData || typeof bureauData !== 'object') return {};

  const candidates = [field];
  if (field === 'account_status') candidates.push('status');
  if (field === 'status') candidates.push('account_status');
  if (field === 'date_reported') candidates.push('last_reported');
  if (field === 'last_reported') candidates.push('date_reported');
  if (field === 'date_last_payment') candidates.push('date_of_last_payment');
  if (field === 'date_of_last_payment') candidates.push('date_last_payment');
  if (field === 'remarks') candidates.push('comments');
  if (field === 'comments') candidates.push('remarks');

  for (const key of candidates) {
    const data = bureauData[key];
    if (data && typeof data === 'object' && Object.keys(data).length) return data;
  }
  return {};
};

const paymentStatusByBureau = (account) => {
  const result = {};
  const fieldData = bureauFieldMap(account, 'payment_status');
  Object.entries(fieldData).forEach(([bureau, value]) => {
    if (value != null && String(value).trim()) {
      result[bureau] = String(value).trim();
    }
  });
  if (account?.payment_status && !Object.keys(result).length) {
    result.Account = String(account.payment_status).trim();
  }
  return result;
};

const collectLateHistoryCells = (account) => {
  const cells = [];
  const grid = account?.payment_history_grid;
  if (!grid || typeof grid !== 'object') return cells;

  Object.entries(grid).forEach(([bureau, years]) => {
    if (!years || typeof years !== 'object') return;
    Object.entries(years).forEach(([year, months]) => {
      if (!months || typeof months !== 'object') return;
      const yearSuffix = String(year).slice(-2);
      Object.entries(months).forEach(([month, status]) => {
        if (!isLatePaymentStatus(status)) return;
        const monthLabel = MONTH_LABELS[month] || MONTH_LABELS[Number(month)] || String(month);
        cells.push({
          bureau,
          month: `${monthLabel} ${yearSuffix}`,
          status: String(status).trim().toUpperCase(),
        });
      });
    });
  });
  return cells;
};

const hasPastDueAmount = (account) => {
  const pastDue = account?.past_due ?? account?.past_due_amount;
  if (pastDue == null || pastDue === '' || pastDue === 0 || pastDue === '0') return false;
  const numeric = Number(String(pastDue).replace(/[$,]/g, ''));
  return Number.isFinite(numeric) && numeric > 0;
};

const hasPositiveLateCounts = (account) =>
  LATE_COUNT_FIELDS.some((field) => {
    const value = account?.[field];
    if (!value) return false;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0;
  });

const narrativeNegativeReasons = (account) => {
  const reasons = [];
  const blobs = [
    ['account_status', account?.account_status || account?.status],
    ['remarks', account?.remarks || account?.comments],
    ['rating', account?.rating || account?.account_rating],
    ['account_type', account?.account_type || account?.type],
    ['classification', account?.classification],
  ];
  blobs.forEach(([label, value]) => {
    const text = normalizeText(value);
    if (!text) return;
    if (
      isNegativePaymentStatus(value)
      || [
        'charge off',
        'charged off',
        'charge-off',
        'collection',
        'paid charge off',
        'settled for less',
        'foreclosure',
        'repossession',
        'late payment',
        'delinquent',
        'serious delinquency',
        'past due',
        'derogatory',
      ].some((token) => text.includes(token))
    ) {
      reasons.push(`${label.replace(/_/g, ' ')} indicates ${String(value).trim()}`);
    }
  });
  return reasons;
};

const detectBureauDifferences = (account) => {
  const reasons = [];
  const seen = new Set();

  BUREAU_COMPARE_FIELDS.forEach((field) => {
    const fieldData = bureauFieldMap(account, field);
    const values = Object.entries(fieldData)
      .map(([bureau, raw]) => {
        if (raw == null) return null;
        const text = String(raw).trim();
        if (!text || text === '--' || text.toLowerCase() === 'n/a') return null;
        return [bureau, text];
      })
      .filter(Boolean);

    if (values.length < 2) return;
    const unique = new Set(values.map(([, v]) => v.toLowerCase()));
    if (unique.size <= 1) return;
    if (seen.has(field)) return;
    seen.add(field);
    reasons.push(
      `Bureau difference on ${field.replace(/_/g, ' ')}: ${values
        .slice(0, 3)
        .map(([b, v]) => `${b}=${v}`)
        .join(', ')}`
    );
  });

  return reasons;
};

const isOpenCurrentClean = (account, lateCells, negativeReasons) => {
  if (lateCells.length || negativeReasons.length) return false;
  const accountStatus = normalizeText(account?.account_status || account?.status);
  const paymentStatus = normalizeText(account?.payment_status);
  if (!accountStatus && !paymentStatus) return false;

  const statusOk =
    ['open', 'current', 'active', 'paid', 'closed', 'pays as agreed'].includes(accountStatus)
    || isCleanPositiveStatus(accountStatus);
  const paymentOk =
    ['current', 'ok', 'pays as agreed', 'paid as agreed', 'never late', 'paid'].includes(paymentStatus)
    || isCleanPositiveStatus(paymentStatus);

  if (accountStatus && !statusOk) return false;
  if (paymentStatus && !paymentOk) return false;
  return Boolean(accountStatus || paymentStatus);
};

export const classifyAccountEvidence = (account) => {
  if (!account) {
    return {
      isNegative: false,
      severity: SEVERITY_UNKNOWN,
      aiTags: ['UNKNOWN'],
      negativeReasons: [],
      bureauDifferenceReasons: [],
      lateHistoryCells: [],
      paymentStatusByBureau: {},
    };
  }

  // Prefer server evidence when present and complete.
  const serverEvidence = account.classification_evidence;
  if (
    serverEvidence
    && serverEvidence.severity
    && Array.isArray(serverEvidence.aiTags)
  ) {
    return {
      isNegative: Boolean(serverEvidence.isNegative),
      severity: serverEvidence.severity,
      aiTags: serverEvidence.aiTags,
      negativeReasons: serverEvidence.negativeReasons || [],
      bureauDifferenceReasons: serverEvidence.bureauDifferenceReasons || [],
      lateHistoryCells: serverEvidence.lateHistoryCells || [],
      paymentStatusByBureau: paymentStatusByBureau(account),
      account_name: accountName(account),
      account_status: account.account_status || account.status || null,
      payment_status: account.payment_status || null,
      derogatory_reason: (serverEvidence.negativeReasons || []).join('; ') || null,
      counted_as_negative: Boolean(serverEvidence.isNegative),
    };
  }

  const byBureau = paymentStatusByBureau(account);
  const lateHistoryCells = collectLateHistoryCells(account);
  const negativeReasons = [];
  const aiTags = [];

  Object.entries(byBureau).forEach(([bureau, status]) => {
    if (!isNegativePaymentStatus(status)) return;
    negativeReasons.push(`Payment status ${status} reported by ${bureau}`);
    if (isChargeOffStatus(status)) aiTags.push('CHARGE OFF');
    else if (isCollectionStatus(status)) aiTags.push('COLLECTION');
    else aiTags.push('LATE PAYMENT');
  });

  if (account.payment_status && isNegativePaymentStatus(account.payment_status)) {
    const reason = `Payment status ${account.payment_status}`;
    if (!negativeReasons.some((r) => r.toLowerCase().includes(String(account.payment_status).trim().toLowerCase()))) {
      negativeReasons.push(reason);
    }
    if (isChargeOffStatus(account.payment_status)) aiTags.push('CHARGE OFF');
    else if (isCollectionStatus(account.payment_status)) aiTags.push('COLLECTION');
    else aiTags.push('LATE PAYMENT');
  }

  lateHistoryCells.forEach((cell) => {
    const status = cell.status || '';
    if (normalizePaymentStatus(status) === 'CO' || ['CO', 'COLL'].includes(String(status).toUpperCase())) {
      negativeReasons.push(`${cell.bureau} ${cell.month} reported charge-off`);
      aiTags.push('CHARGE OFF');
    } else {
      negativeReasons.push(`${cell.bureau} ${cell.month} reported ${status} days late`);
      aiTags.push('LATE PAYMENT');
    }
  });

  negativeReasons.push(...narrativeNegativeReasons(account));
  if (hasPastDueAmount(account)) {
    negativeReasons.push('Past due amount reported');
    aiTags.push('LATE PAYMENT');
  }
  if (hasPositiveLateCounts(account)) {
    negativeReasons.push('Late payment counts reported');
    aiTags.push('LATE PAYMENT');
  }
  if (normalizeText(account.account_type || account.type).includes('collection')) {
    negativeReasons.push('Collection account type');
    aiTags.push('COLLECTION');
  }

  const uniqueReasons = [...new Set(negativeReasons)];
  const uniqueTags = [...new Set(aiTags)];

  let bureauDifferenceReasons = [];
  if (!uniqueReasons.length && !lateHistoryCells.length) {
    bureauDifferenceReasons = detectBureauDifferences(account);
  }

  let severity;
  let finalTags;
  if (uniqueReasons.length || lateHistoryCells.length) {
    severity = SEVERITY_NEGATIVE;
    finalTags = [...uniqueTags];
    if (!finalTags.includes('DISPUTABLE')) finalTags.push('DISPUTABLE');
    if (!finalTags.some((t) => ['LATE PAYMENT', 'CHARGE OFF', 'COLLECTION'].includes(t))) {
      finalTags.unshift('LATE PAYMENT');
    }
  } else if (bureauDifferenceReasons.length) {
    severity = SEVERITY_BUREAU_DIFFERENCE;
    finalTags = ['BUREAU DIFFERENCE', 'DISPUTABLE'];
  } else if (isOpenCurrentClean(account, lateHistoryCells, uniqueReasons)) {
    severity = SEVERITY_CURRENT;
    finalTags = ['CURRENT'];
  } else {
    severity = SEVERITY_UNKNOWN;
    finalTags = ['UNKNOWN'];
  }

  const isNegative = severity === SEVERITY_NEGATIVE;
  return {
    isNegative,
    severity,
    aiTags: finalTags,
    negativeReasons: uniqueReasons,
    bureauDifferenceReasons,
    lateHistoryCells,
    paymentStatusByBureau: byBureau,
    account_name: accountName(account),
    account_status: account.account_status || account.status || null,
    payment_status: account.payment_status || null,
    derogatory_reason: uniqueReasons.length ? uniqueReasons.join('; ') : null,
    counted_as_negative: isNegative,
  };
};

export const getAccountSeverity = (account) => classifyAccountEvidence(account).severity;

export const getDerogatoryReasons = (account) => classifyAccountEvidence(account).negativeReasons || [];

export const classifyAccountNegative = (account) => {
  const evidence = classifyAccountEvidence(account);
  return {
    account_name: evidence.account_name,
    account_status: evidence.account_status,
    payment_status: evidence.payment_status,
    derogatory_reason: evidence.derogatory_reason,
    counted_as_negative: evidence.counted_as_negative,
    severity: evidence.severity,
    aiTags: evidence.aiTags,
    negativeReasons: evidence.negativeReasons,
    bureauDifferenceReasons: evidence.bureauDifferenceReasons,
    lateHistoryCells: evidence.lateHistoryCells,
  };
};

const LATE_DAY_LABELS = {
  30: 'Late 30 Days',
  60: 'Late 60 Days',
  90: 'Late 90 Days',
  120: 'Late 120 Days',
};

const lateDaysFromStatus = (status) => {
  const text = String(status || '').trim().toLowerCase();
  const explicit = text.match(/late\s*(\d+)/);
  if (explicit) {
    const days = Number(explicit[1]);
    return LATE_DAY_LABELS[days] || `Late ${explicit[1]} Days`;
  }
  const normalized = normalizePaymentStatus(status);
  const severity = paymentStatusSeverity(normalized);
  const dayMap = { 2: 30, 3: 60, 4: 90, 5: 120, 6: 150, 7: 180 };
  const days = dayMap[severity];
  if (days && LATE_DAY_LABELS[days]) return LATE_DAY_LABELS[days];
  if (severity >= 2) return 'Historical Late Payments';
  return null;
};

const formatNegativePaymentStatusLabel = (status) => {
  if (!status) return null;
  const text = String(status).trim();
  const lower = text.toLowerCase();
  if (isCollectionStatus(status)) return 'Collection';
  if (isChargeOffStatus(status)) return 'Charge-Off';
  if (lower.includes('past due')) return 'Past Due';
  if (lower.includes('derogatory')) return 'Derogatory';
  if (isLatePaymentStatus(status) || lower.includes('late') || lower.includes('delinquent')) {
    return lateDaysFromStatus(status) || 'Historical Late Payments';
  }
  if (isNegativePaymentStatus(status)) return text;
  return null;
};

const maxLateDaysFromHistory = (lateHistoryCells = []) => {
  let maxSeverity = 0;
  lateHistoryCells.forEach((cell) => {
    const severity = paymentStatusSeverity(cell?.status);
    if (severity > maxSeverity) maxSeverity = severity;
  });
  const dayMap = { 2: 30, 3: 60, 4: 90, 5: 120, 6: 150, 7: 180 };
  return dayMap[maxSeverity] || null;
};

const accountIsCurrentlyClean = (account) => {
  const paymentStatus = normalizeText(account?.payment_status);
  const accountStatus = normalizeText(account?.account_status || account?.status);
  const paymentClean =
    !paymentStatus
    || isCleanPositiveStatus(paymentStatus)
    || ['current', 'paid', 'pays as agreed', 'paid as agreed', 'ok', 'never late'].includes(paymentStatus);
  const statusClean =
    !accountStatus
    || isCleanPositiveStatus(accountStatus)
    || ['open', 'current', 'active', 'paid', 'closed', 'pays as agreed'].includes(accountStatus);
  return paymentClean && statusClean;
};

const collectActiveNegativePaymentStatuses = (account, paymentStatusByBureau = {}) => {
  const statuses = [];
  if (account?.payment_status && isNegativePaymentStatus(account.payment_status)) {
    statuses.push(account.payment_status);
  }
  Object.values(paymentStatusByBureau || {}).forEach((status) => {
    if (status && isNegativePaymentStatus(status)) statuses.push(status);
  });
  return statuses;
};

/**
 * User-facing negative reason for dispute wizard / account lists.
 * Never returns bare account_status values like "Current" or "Open".
 */
export const getNegativeDisplayReason = (account) => {
  const evidence = classifyAccountEvidence(account);
  if (!evidence.isNegative) return null;

  const activeNegativeStatuses = collectActiveNegativePaymentStatuses(
    account,
    evidence.paymentStatusByBureau
  );
  for (const status of activeNegativeStatuses) {
    const label = formatNegativePaymentStatusLabel(status);
    if (label) return label;
  }

  const lateDays = maxLateDaysFromHistory(evidence.lateHistoryCells);
  if (lateDays) {
    if (accountIsCurrentlyClean(account)) {
      return 'Current • Historical Late Payment';
    }
    return LATE_DAY_LABELS[lateDays] || 'Historical Late Payments';
  }

  if (hasPastDueAmount(account)) return 'Past Due';

  const tags = evidence.aiTags || [];
  if (tags.includes('CHARGE OFF')) return 'Charge-Off';
  if (tags.includes('COLLECTION')) return 'Collection';
  if (tags.includes('LATE PAYMENT')) return 'Historical Late Payments';

  const narrative = (evidence.negativeReasons || []).join(' ').toLowerCase();
  if (narrative.includes('derogatory')) return 'Derogatory';
  if (narrative.includes('past due')) return 'Past Due';

  return 'Historical Late Payments';
};

export const isNegativeAccount = (account) => classifyAccountEvidence(account).isNegative;

export const filterNegativeTradelines = (tradelines = []) =>
  (tradelines || []).filter((tradeline) => isNegativeAccount(tradeline));


export const getPrimaryDisplayTag = (account) => {
  const evidence = classifyAccountEvidence(account);
  for (const tag of evidence.aiTags || []) {
    if (['LATE PAYMENT', 'CHARGE OFF', 'COLLECTION', 'BUREAU DIFFERENCE'].includes(tag)) {
      return tag;
    }
  }
  // Never invent REVIEW without evidence.
  return null;
};

export const logNegativeClassificationDebug = (accounts = []) => {
  if (process.env.NODE_ENV !== 'development') return;
  accounts.forEach((account) => {
    // eslint-disable-next-line no-console
    console.info('[negative_classification]', classifyAccountNegative(account));
  });
};
